<template>
  <div
    v-if="badge && badgeMessage"
    class="o-badge o-typography__title2 u-bold u-uppercase"
    :class="badgeClasses"
    :style="{ backgroundColor: badge.color }"
  >
    <span class="o-badge__text">
      {{ badgeMessage }}
      <div
        v-if="isCountdownVisible"
        class="o-badge__time"
      >
        {{ badgeTime }}
      </div>
    </span>
  </div>
</template>
<script>
  import dayjs from 'dayjs';
  import { productAvailabilityMixin } from '@/index/shared/mixins';
  import { ProductType } from '@/shared/enums';
  import { timeFormatCountdown } from '@/shared/utils/time';

  const BADGE_TYPE_CUSTOM = 'custom';
  const ONE_DAY_IN_SECONDS = 24 * 60 * 60;

  const ScheduleType = {
    EXCLUSIVE: 'EXCLUSIVE',
    PREMIERE: 'PREMIERE',
    SOON: 'SOON',
  };

  const Badge = {
    LIVE_NOW: {
      type: 'live-now',
      message: 'product.badge.live',
    },
    BITE_EXCLUSIVE: {
      type: 'bite-exclusive',
      message: 'product.badge.exclusive',
    },
    ORIGINALS: {
      type: 'originals',
      message: 'product.badge.originals',
    },
    ONLY_PREMIUM: {
      type: 'only-premium',
      message: 'product.badge.premium',
    },
    PREMIERE: {
      type: 'premiere',
      message: 'product.badge.premiere',
    },
    CATCH_UP: {
      type: 'catch-up',
      message: 'product.badge.catchUp',
    },
    NEW: {
      type: 'new',
      message: 'product.badge.new',
    },
    LAST_CHANCE: {
      type: 'last-chance',
      message: 'product.badge.lastChance',
    },
    SOON: {
      type: 'soon',
      message: 'product.badge.soon',
    },
    SPECIAL_OFFER: {
      type: 'special-offer',
      message: 'product.badge.specialOffer',
    },
  };

  const INITIAL_BADGE_TIME = '00:00';

  export default {
    name: 'VBadge',
    mixins: [
      productAvailabilityMixin,
    ],
    props: {
      product: Object,
      showIcon: {
        type: Boolean,
        default: false,
      },
      counting: Boolean,
    },
    data() {
      return {
        info: this.$services.resolve('info'),
        time: this.$services.resolve('time'),
        badges: this.$services.resolve('badges'),
        badgeTime: INITIAL_BADGE_TIME,
        isFinished: false,
      };
    },
    computed: {
      badgeClasses() {
        return [`o-badge--${this.badge.type}`, {
          'o-badge--has-countdown': this.isCountdownVisible,
          'is-finished': this.isFinished,
          'is-counting': this.counting,
        }];
      },
      badgeMessage() {
        return this.$t(this.badge.message);
      },
      isCountdownVisible() {
        return this.badgeTime !== INITIAL_BADGE_TIME;
      },
      badge() {
        const now = this.time.now;
        const { catchupTill, since, till, type } = this.product;

        if (type === ProductType.PROGRAMME && since && dayjs(since).isBefore(now, 'ms') && (!till || dayjs(till).isAfter(now, 'ms'))) {
          return Badge.LIVE_NOW;
        }

        if (type === ProductType.PROGRAMME && since && dayjs(since).isAfter(now, 'ms')) {
          return Badge.SOON;
        }

        if (this.currentSchedule?.type === ScheduleType.EXCLUSIVE) {
          return Badge.BITE_EXCLUSIVE;
        }

        if (this.customBadge) {
          return this.customBadge;
        }

        if (this.currentSchedule?.type === ScheduleType.PREMIERE) {
          return Badge.PREMIERE;
        }

        if (type === ProductType.PROGRAMME && till && catchupTill && dayjs(now).isBetween(till, catchupTill)) {
          return Badge.CATCH_UP;
        }

        if (this.currentSchedule?.type === ScheduleType.SOON) {
          return Badge.SOON;
        }

        if (type !== ProductType.PROGRAMME && this.currentSchedule) {
          const newBadgeTime = this.getBadgeTime(Badge.NEW);

          if (newBadgeTime) {
            const minDate = dayjs(this.currentSchedule.since).add(newBadgeTime.hours, 'h');

            if (minDate > now) {
              return Badge.NEW;
            }
          }

          if (this.currentSchedule.till) {
            const lastChanceBadgeTime = this.getBadgeTime(Badge.LAST_CHANCE);

            if (lastChanceBadgeTime) {
              const maxDate = dayjs(this.currentSchedule.till).subtract(lastChanceBadgeTime.hours, 'h');

              if (maxDate < now) {
                return Badge.LAST_CHANCE;
              }
            }
          }
        }

        return null;
      },
      customBadge() {
        const badge = this.badges.get(this.product.badge?.id);

        if (!badge || !this.getProductAvailability(badge)) {
          return;
        }

        const { badgeTitle, color } = badge;

        return {
          message: badgeTitle,
          color: color ? `#${color}` : '',
          type: BADGE_TYPE_CUSTOM,
        };
      },
      currentSchedule() {
        const now = this.time.now;
        const { displaySchedules = [] } = this.product;

        return displaySchedules.find((schedule) => {
          return dayjs(schedule.since).isBefore(now, 'ms') && (!schedule.till || dayjs(schedule.till).isAfter(now, 'ms'));
        });
      },
      isProgramme() {
        return this.product.type === ProductType.PROGRAMME;
      },
      isSoon() {
        return this.badge?.type === Badge.SOON.type;
      },
    },
    watch: {
      counting: {
        immediate: true,
        handler(value) {
          if (!this.isSoon) {
            return;
          }

          this.stopUpdating();
          this.setCountdownTime();

          if (value) {
            this.countdown();
          }
        },
      },
    },
    beforeDestroy() {
      this.stopUpdating();
    },
    methods: {
      countdown() {
        const timeout = 1000;

        this.updateTimeout = setTimeout(() => {
          this.setCountdownTime();
          this.countdown();
        }, timeout);
      },
      stopUpdating() {
        clearTimeout(this.updateTimeout);
      },
      getBadgeTime(badge) {
        return this.info.badgeTimes.find((time) => time.badgeType === badge.type && time.productType === this.product.type);
      },
      setCountdownTime() {
        const seconds = this.getSecondsToStart();

        if (!seconds || seconds < 0 || seconds > ONE_DAY_IN_SECONDS) {
          this.badgeTime = INITIAL_BADGE_TIME;
          this.isFinished = seconds <= 0;
          return;
        }

        this.badgeTime = timeFormatCountdown(seconds);
      },
      getSecondsToStart() {
        const now = this.time.now;

        if (this.isProgramme) {
          return dayjs(this.product.sinceDate).diff(now, 's');
        }

        return this.currentSchedule?.till && dayjs(this.currentSchedule.till).diff(now, 's');
      },
    },
  };
</script>
